import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import { useI18n } from 'next-localization';

import { isArray, isEmpty, isEqual } from 'lodash-es';
import { FormProvider, useForm } from 'react-hook-form';

import { ChangeLocationModal } from '@/components/change-location';
import { FormRadio } from '@/components/form/form-radio';
import { FormTextarea } from '@/components/form/form-textarea';
import { useModalContext } from '@/components/modals/modal-context';
import { ProviderLocation } from '@/features/profile/profile.types';
import {
  CELEBRUS_APPOINTMENT_SELF_PAY,
  CELEBRUS_CONTINUE_BUTTON_ID_2,
  CELEBRUS_REASON_FOR_VISIT_TEXTAREA_ID,
} from '@/lib/constants';
import { physicianDisplayTitle } from '@/lib/utils';
import { NeuButton } from '@hcahealthcaredpx/neutron-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';

import styles from '../../booking.module.css';
import {
  BookAppointmentHoldResponse,
  BookAppointmentSearchResponse,
  StepOneProps,
  Timeslot,
} from '../../booking.types';
import { useBookingContext } from '../../booking-context';
import { useBookAppointmentHoldMutation } from '../../hooks/use-book-appointment-hold';
import { useBookAppointmentSearchMutation } from '../../hooks/use-book-appointment-search';
import { Timeslots } from '../../hooks/use-timeslots';
import { NavigationButtons } from '../navigation-buttons';
import Section from '../section';
import { Alert } from './calendar/alert';
import { Calendar } from './calendar/calendar';
import { AppointmentLocation } from './appointment-location';
import { AppointmentTypeSelect } from './appointment-type-select';
import {
  HOLD_REMAINING_TIMEOUT,
  MAX_CHARACTERS_REASON_FOR_VISIT,
  NUMBER_DAYS_OUT,
  SELF_PAYOR_YES_NO_OPTIONS,
  SLOT_RELEASED_TIMEOUT,
  StepOneFormKeys,
  TELEHEALTH_APPOINTMENT_TYPE_ID,
  TELEHEALTH_MODAL,
  TIME_NOT_BOOKED_ERROR_MODAL,
  TIME_RELEASED_MODAL,
  TIME_REMAINING_MODAL,
  TIME_UNAVAILABLE_MODAL,
} from './constants';
import { stepOneSchema } from './schema';
import { TeleHealthModal } from './telehealth-modal';
import { getAvailabeAppointmentTypes, getNextFormatted, getTodayDateFormatted } from './utils';

export const StepOne = forwardRef(
  (
    {
      fields,
      providerLocations,
      location,
      goToNextStep,
      goToPreviousStep,
      timeRemainingRef,
      timeReleasedRef,
      slots,
      setSlots,
      selectedTimeslot,
      setSelectedTimeslot,
    }: StepOneProps & Timeslots,
    stepOneRef
  ): JSX.Element => {
    const queryClient = useQueryClient();
    const { t } = useI18n();
    const { physicianFullName, physicianData, mhdAppTypes, siteType } = fields;

    const { stepOneData, setStepOneData } = useBookingContext();

    const form = useForm({
      mode: 'onChange',
      resolver: zodResolver(stepOneSchema(t)),
      defaultValues: stepOneData,
    });

    const { watch } = form;
    const isSelfPayor = watch(StepOneFormKeys.selfPayor);
    const newPatient = watch(StepOneFormKeys.newPatient);
    const isNewPatient = useMemo(() => Boolean(parseInt(newPatient)), [newPatient]);

    const appointmentTypes = getAvailabeAppointmentTypes(mhdAppTypes, isNewPatient);

    const holdMutation = useBookAppointmentHoldMutation({
      onSuccess: (response: BookAppointmentHoldResponse) => {
        if (isEmpty(response.data)) {
          openModal(TIME_UNAVAILABLE_MODAL);
        }

        if (timeRemainingRef.current) {
          clearTimeout(timeRemainingRef.current);
        }
        if (timeReleasedRef.current) {
          clearTimeout(timeReleasedRef.current);
        }
        timeRemainingRef.current = null;
        timeReleasedRef.current = null;
        timeRemainingRef.current = setTimeout(() => {
          openModal(TIME_REMAINING_MODAL);
          if (timeReleasedRef.current) {
            timeReleasedRef.current = null;
          }
          if (timeRemainingRef.current) {
            timeRemainingRef.current = null;
          }
          timeReleasedRef.current = setTimeout(() => {
            setSelectedTimeslot(null);
            // Reset form
            setStepOneData((data) => {
              return {
                selfPayor: data.selfPayor,
                newPatient: data.newPatient,
                appointmentType: data.appointmentType,
                reasonForVisit: data.reasonForVisit,
              };
            });
            closeModal(TIME_REMAINING_MODAL);
            closeModal(TIME_NOT_BOOKED_ERROR_MODAL);
            openModal(TIME_RELEASED_MODAL);
          }, SLOT_RELEASED_TIMEOUT);
        }, HOLD_REMAINING_TIMEOUT);

        setStepOneData((data) => {
          const parsedDateTime = response.data?.dateTimeOfAppointment
            ? response.data?.dateTimeOfAppointment
            : '';

          return {
            ...data,
            // Todo: this has to improve sometimes api returns empty json {} (for hold endpoint)
            holdId: response.data?.holdId || '',
            dateTimeOfAppointment: response.data?.dateTimeOfAppointment || '',
            date: selectedTimeslot ? selectedTimeslot.date : parsedDateTime,
            appointmentId: response.data?.appointmentId || '',
          };
        });

        setSelectedTimeslot((timeSlot: Timeslot) => {
          return {
            ...timeSlot,
            holdId: response.data?.holdId || '',
          };
        });
      },
      onError: () => {
        setStepOneData((data) => {
          return {
            ...data,
            holdId: '',
            dateTimeOfAppointment: '',
            appointmentId: '',
          };
        });
        setSelectedTimeslot(null);
        openModal(TIME_UNAVAILABLE_MODAL);
      },
    });

    const { mutate, isPending, isError } = useBookAppointmentSearchMutation({
      onSuccess: (response: BookAppointmentSearchResponse) => {
        if (isArray(response.data)) {
          // Note: Make sure data is sorted by day ascending
          // as new .NET api layer might return unsorted data
          const sortedDataByDate = response.data.sort((a, b) => a.day.localeCompare(b.day));

          setSlots((current) => [...current, ...sortedDataByDate]);
        }

        if (isEmpty(response)) {
          setSlots([]);
        }

        queryClient.invalidateQueries({ queryKey: ['book-appointment-search'] });
      },
      onError: (e) => {
        if (e.cause === 400) {
          openModal(TIME_UNAVAILABLE_MODAL);
        }
      },
    });

    const physicianName = useMemo(
      () => physicianDisplayTitle(physicianFullName, physicianData.physicianDesignation),
      [physicianData, physicianFullName]
    );

    const appointmentType = watch(StepOneFormKeys.appointmentType);
    const { openModal, closeModal } = useModalContext();

    const fetchDaysOut = useCallback(
      (
        startDate: string,
        appointmentTypeId?: string,
        newLocation?: Pick<ProviderLocation, 'id' | 'mhdCalendarId'>
      ) => {
        mutate({
          firstAvailable: false,
          newPatient: false,
          numberDaysOut: NUMBER_DAYS_OUT,
          physicianNpi: physicianData.physicianNpi,
          calendarId: newLocation?.mhdCalendarId || location.mhdCalendarId,
          providerLocationId: newLocation?.id || location.id,
          appointmentTypeIds: [appointmentTypeId || appointmentType],
          startDate: startDate, // next day
          siteType: siteType,
          source: 'dotcms', // todo: check if this is correct
        });
      },
      [
        appointmentType,
        location.id,
        location.mhdCalendarId,
        physicianData.physicianNpi,
        mutate,
        siteType,
      ]
    );

    const clearSlotsAndFetch = useCallback(
      (appointmentType: string, newLocation?: Pick<ProviderLocation, 'id' | 'mhdCalendarId'>) => {
        if (isEmpty(appointmentType)) return;

        if (timeRemainingRef.current) {
          clearTimeout(timeRemainingRef.current);
        }
        if (timeReleasedRef.current) {
          clearTimeout(timeReleasedRef.current);
        }
        setSlots([]);
        const todayFormatted = getTodayDateFormatted();
        fetchDaysOut(todayFormatted, appointmentType, newLocation);
      },
      [setSlots, fetchDaysOut, timeRemainingRef, timeReleasedRef]
    );

    const onApptTypeSelect = useCallback(
      (appointmentTypeId: string) => {
        if (appointmentTypeId === 'select') return;
        // clear for now, check for caching in React Query
        clearSlotsAndFetch(appointmentTypeId);

        if (sessionStorage.getItem('telehealthModalHasBeenShown')) {
          return;
        }

        if (appointmentTypeId === TELEHEALTH_APPOINTMENT_TYPE_ID) {
          openModal(TELEHEALTH_MODAL);
          sessionStorage.setItem('telehealthModalHasBeenShown', '1');
        }
      },
      [clearSlotsAndFetch, openModal]
    );

    useEffect(() => {
      const notFound = slots.every((x) => isEmpty(x.firstAvailable));
      // important: keep the block expression for the length check
      const shouldFetchAgain =
        slots.length > 0 && notFound && slots.length <= parseInt(NUMBER_DAYS_OUT) * 3;
      if (!isPending && !isError && shouldFetchAgain) {
        const lastDayInCollection = slots[slots.length - 1].day;
        const nextDayFormatted = getNextFormatted(lastDayInCollection);
        /* Note: legacy makes 3 requests for the next 3 x 62 days -> */
        // Todo: fix loop in case of BR returning {} constantly.
        // Maybe in this case check for max retries as,
        // there seems to be a case where BE returns empty {} and we call this again
        // because isPending is set to false and slots are less than max days out.
        fetchDaysOut(nextDayFormatted);
      }
    }, [slots, fetchDaysOut, isPending, isError]);

    const hideBookingIfSelfPayor = useMemo(() => !!parseInt(isSelfPayor), [isSelfPayor]);

    const reasonForVisitText = watch(StepOneFormKeys.reasonForVisit);
    const reasonForVisitTip = useMemo(() => {
      if (reasonForVisitText.length) {
        return t('OAS.Booking.Appointment.ReasonForVisitTip', {
          reasonForVisit: reasonForVisitText.length,
          maxCharactersReason: MAX_CHARACTERS_REASON_FOR_VISIT,
        });
      }
      return t('OAS.Booking.Appointment.AnythingElseMessage');
    }, [reasonForVisitText, t]);

    const appointmentTypeLabel = appointmentTypes.find((item) =>
      appointmentType ? item.value === appointmentType : item.selected
    )?.label;

    const handleSubmit = form.handleSubmit((formData) => {
      const stepOneDateTimeAppt = stepOneData?.date;
      const selectedTimeslotDate = selectedTimeslot?.date;

      const isDifferentSlot = !isEqual(stepOneDateTimeAppt, selectedTimeslotDate);

      if (selectedTimeslot && isDifferentSlot) {
        holdMutation.mutate({
          appointmentTypeId: appointmentType,
          calendarId: location.mhdCalendarId,
          providerLocationId: location.id,
          appointmentId: selectedTimeslot.appointmentId,
          date: selectedTimeslot?.date || '',
          duration: selectedTimeslot.duration,
          startTime: selectedTimeslot.startTime,
        });
      }

      setStepOneData((prevData) => {
        return {
          ...prevData,
          ...formData,
          physician: physicianData, // Todo: remove this we consume it trough props, no need to pass it trough context
          providerLocationId: location.mhdCalendarId,
          location,
          appointmentTypeLabel,
        };
      });
      goToNextStep();
    });

    const calendarRef = React.useRef<HTMLDivElement>(null);
    useImperativeHandle(stepOneRef, () => {
      return {
        resetTimes: () => {
          clearSlotsAndFetch(appointmentType);
        },
        calendarRef,
      };
    }, [appointmentType, clearSlotsAndFetch]);

    const handleChangeLocationCallback = useCallback(
      (newLocation: Pick<ProviderLocation, 'id' | 'mhdCalendarId'>) => {
        clearSlotsAndFetch(appointmentType, newLocation);
      },
      [appointmentType, clearSlotsAndFetch]
    );

    const isContinueDisabled = !form.formState.isValid || !selectedTimeslot;
    const selectedLocation = providerLocations.find((loc) => loc.id === location.id) || location;

    // Note: once the user selects newPatient yes/no, and there are no appointment types available,
    // we hide the rest of the form.
    // Refer to: https://dev.azure.com/hcaDX/DPX-Teams-Patient-Engagement/_workitems/edit/207991/
    const showRestOfTheForm =
      isEmpty(newPatient) || (!isEmpty(newPatient) && appointmentTypes.length > 1);

    return (
      <FormProvider {...form}>
        <form onSubmit={handleSubmit} noValidate>
          <Section title={t('OAS.Booking.Appointment.Location')}>
            <AppointmentLocation location={selectedLocation} />
            <div className="d-flex justify-content-lg-end mt-2">
              {providerLocations?.length > 1 && (
                <ChangeLocationModal
                  locations={providerLocations}
                  changeLocationCallback={handleChangeLocationCallback}
                  providerId={physicianData.physicianNpi}
                  showLinkButton={false}
                >
                  <NeuButton className="continue" kind="primary" color="blue" type="button">
                    {t('OAS.Common.ChangeLocation')} ({providerLocations?.length})
                  </NeuButton>
                </ChangeLocationModal>
              )}
            </div>
          </Section>
          <Section title={t('OAS.Booking.Appointment.SelfPay')}>
            <FormRadio
              label={t('OAS.Booking.Appointment.SelfPayor')}
              name={StepOneFormKeys.selfPayor}
              radioOptions={SELF_PAYOR_YES_NO_OPTIONS}
              required
              id={CELEBRUS_APPOINTMENT_SELF_PAY}
            />

            {hideBookingIfSelfPayor && (
              <p className="mt-3">
                {t('OAS.Booking.Appointment.CallOurOffice')}{' '}
                <a href={`tel:${location.phone}`} aria-label={`Call ${location.phone}`}>
                  {location.phone}
                </a>
              </p>
            )}
          </Section>
          {!hideBookingIfSelfPayor && (
            <>
              <AppointmentTypeSelect
                physicianName={physicianName}
                location={location}
                appointmentType={appointmentType}
                appointmentTypes={appointmentTypes}
                newPatient={newPatient}
                onSelect={onApptTypeSelect}
              />
              {showRestOfTheForm && (
                <>
                  <Section title={t('OAS.Booking.Appointment.DescribeReason')}>
                    <FormTextarea
                      label={
                        t('OAS.Booking.StepOne.ReasonForVisit') ||
                        t('OAS.Booking.Confirmation.ReasonForVisit')
                      }
                      labelHidden
                      name={StepOneFormKeys.reasonForVisit}
                      tip={reasonForVisitTip}
                      required
                      rows={4}
                      id={CELEBRUS_REASON_FOR_VISIT_TEXTAREA_ID}
                    />
                  </Section>
                  <Section title={t('OAS.Booking.Appointment.SelectApptTime')}>
                    {!isEmpty(appointmentType) && <Alert />}

                    {!appointmentType.length && (
                      <div className={styles.selectTimeWrapper}>
                        <br />
                        <br />
                        <h4 className="neu-display-5">
                          {t('OAS.Booking.Appointment.PleaseCompleteInformation')}
                        </h4>
                      </div>
                    )}
                    {!!appointmentType.length && (
                      <div ref={calendarRef}>
                        <Calendar
                          locationPhone={location.phone}
                          search={slots}
                          isLoading={isPending}
                          handleNextScan={fetchDaysOut}
                          selectedTimeslot={selectedTimeslot?.appointmentId || ''}
                          onTimeslotChange={setSelectedTimeslot}
                        />
                      </div>
                    )}
                  </Section>
                </>
              )}
            </>
          )}
          <NavigationButtons
            isContinueDisabled={isContinueDisabled}
            goToPreviousStep={goToPreviousStep}
            continueButtonId={CELEBRUS_CONTINUE_BUTTON_ID_2}
          />
        </form>
        <TeleHealthModal />
      </FormProvider>
    );
  }
);

StepOne.displayName = 'Step One';
